import { useParams } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useActivityList, useInfiniteActivityList, useProject } from '../../app/queries'
import type { ActivityQuery } from '../../app/activity-query'
import { ScreenLoading } from '../../components/state'
import { collapseActivityThreads, degradeRunningOnDisconnect, degradeStaleRunning, upsertActivityItem, type ActivityItem } from './merge'
import { matchesActivityQuery } from './filter'
import { setActivityDisconnected } from './connection'
import { ActivityView, useActivitySearchState } from './view'
import { createRecoveredWindow } from './recovered-window'
import { useActivityStream } from './stream'

function isExcluded(item: ActivityItem): boolean {
  if (item.requires_action) return false
  if (item.rollback_kind === 'executed') return true
  return Boolean(item.safe_variables?.excluded)
}

type RecoverOpts = { count?: number; deduped?: number }

export function ActivityScreen({ projectId }: { projectId?: string } = {}) {
  const search = useActivitySearchState()
  const lockedProjectId = projectId || search.project_id
  const project = useProject(projectId ?? '')
  const query = useMemo<ActivityQuery>(() => ({
    view: search.view,
    projectId: lockedProjectId,
    type: search.type,
    status: search.status,
    actor: search.actor,
    q: search.q,
    time: search.time,
    scope: search.scope === 'platform' ? 'account' : undefined,
  }), [lockedProjectId, search.actor, search.q, search.scope, search.status, search.time, search.type, search.view])
  const list = useInfiniteActivityList(query)
  const all = useActivityList({
    view: 'all',
    projectId: lockedProjectId,
  })
  const [live, setLive] = useState<ActivityItem[]>([])
  const [disconnected, setDisconnected] = useState(false)
  const [resetNotice, setResetNotice] = useState(false)
  const [recoveredCount, setRecoveredCount] = useState(0)
  const [recoveredDedupe, setRecoveredDedupe] = useState(0)
  const [truthClock, setTruthClock] = useState(() => Date.now())
  const lastUpdated = useRef<string | undefined>(undefined)
  /**
   * 「刚恢复」那个窗口。定时器的取消与轮次都在里面，判据见那个模块。
   *
   * `useRef(createRecoveredWindow)` 那种写法**每次渲染都会新建一个**再扔掉，
   * 而扔掉的那个身上可能挂着一个还没到点的定时器。所以用惰性初始化。
   */
  const recovered = useRef<ReturnType<typeof createRecoveredWindow> | null>(null)
  if (recovered.current === null) recovered.current = createRecoveredWindow()
  const recoveredWindow = recovered.current
  useEffect(() => () => recoveredWindow.dispose(), [recoveredWindow])

  // 设计稿演示态。frame 在生产构建里恒为 undefined（见 search.ts 的 DEV 门禁），
  // 这一段连同它那几个演示用的数字一起是死代码——**门禁写在这儿也写一遍**，
  // 是为了让「这些数是编的」这件事在读代码时就看得见，而不是要读完 search.ts 才知道。
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const hooks = window as typeof window & {
      __npcDisconnectActivity?: () => void
      __npcRecoverActivity?: (opts?: RecoverOpts) => void
    }
    hooks.__npcDisconnectActivity = () => {
      recoveredWindow.disconnected()
      setDisconnected(true)
    }
    hooks.__npcRecoverActivity = (opts) => {
      setDisconnected(false)
      setRecoveredCount(opts?.count ?? 7)
      setRecoveredDedupe(opts?.deduped ?? 2)
    }
    return () => {
      delete hooks.__npcDisconnectActivity
      delete hooks.__npcRecoverActivity
    }
  }, [recoveredWindow])

  useEffect(() => {
    if (!import.meta.env.DEV) return
    if (search.frame === 'A07' || search.frame === 'M05') {
      recoveredWindow.disconnected()
      setDisconnected(true)
    }
    if (search.frame === 'A08') {
      setDisconnected(false)
      setRecoveredCount(7)
      setRecoveredDedupe(2)
    }
  }, [recoveredWindow, search.frame])

  useEffect(() => {
    setActivityDisconnected(disconnected)
    return () => setActivityDisconnected(false)
  }, [disconnected])

  useEffect(() => {
    setLive([])
  }, [search.view, lockedProjectId, search.type, search.status, search.actor, search.q, search.time, search.scope])

  useEffect(() => {
    const timer = window.setInterval(() => setTruthClock(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  useActivityStream({
    /**
     * **恒为真：实时流的生死不跟着列表请求走。**
     *
     * 上一版是 `!list.isError`，而它与自愈接在一起就成了一个闭环陷阱：
     * 流断了 → 自己接回来 → 宣告位置丢了 → `list.refetch()` →
     * **那一次 refetch 失败**（服务端还没完全回来的时候极可能失败）→
     * `list.isError` 变真 → 这个 effect 的依赖变了 → 清理函数跑 →
     * `stream.close()` 把连接关掉、退避定时器一起清掉 → **从此再也不恢复**。
     *
     * 于是「自愈」在最需要它的那一刻自己把自己关了，而屏幕上只剩一行
     * 「连接断开」，看不出是被谁关的。这与 CLAUDE.md §8 那条
     * 「一条能被制造出来的旁路等于没有门」是同一个形状：
     * **一个在故障时自动消失的恢复机制，等于没有恢复机制**，
     * 而故障正是它唯一该干活的时候。
     *
     * 列表拉不到是列表自己的事：那一屏有它自己的错误态与重试入口，
     * 用户照样点得到。两件事分开之后，谁坏了都不会顺手把另一件也关掉。
     */
    enabled: true,
    query,
    onItem: (item) => {
      setLive((current) => upsertActivityItem(current, item))
      lastUpdated.current = item.occurred_at
      if (recoveredWindow.counting()) {
        setRecoveredCount((n) => n + 1)
      }
    },
    onReset: () => {
      setResetNotice(true)
      void list.refetch()
    },
    onDisconnect: () => {
      // 断了：那个窗口连同它排下的定时器一起作废（`disconnected` 里做的）。
      // 不作废的话，上一次 OPEN 排下的定时器会在几十毫秒后把「刚断过」
      // 清成假，于是**后面真正那次恢复不再解除「连接断开」**——
      // 屏幕上那行字再也不会消失，而流其实早就接回来了。
      recoveredWindow.disconnected()
      setDisconnected(true)
    },
    onOpen: () => {
      if (search.frame === 'A07' || search.frame === 'M05') return
      // 回真才是「从断开中恢复」。首次连接回假，那时没什么可解除的。
      if (!recoveredWindow.opened()) return
      setDisconnected(false)
    },
  })

  const merged = useMemo(() => {
    const base = list.data?.pages.flatMap((page) => page.items) ?? []
    const seen = new Set(live.map((item) => item.event_id))
    const extra = live
    const fromList = base.filter((item) => !seen.has(item.event_id))
    const items = extra.concat(fromList).sort((a, b) => {
      const ta = Date.parse(a.occurred_at)
      const tb = Date.parse(b.occurred_at)
      if (tb !== ta) return tb - ta
      return b.stream_seq - a.stream_seq
    })
    const truthful = disconnected ? degradeRunningOnDisconnect(items) : degradeStaleRunning(items, truthClock)
    return collapseActivityThreads(truthful).filter((item) => matchesActivityQuery(item, query))
  }, [list.data?.pages, live, disconnected, query, truthClock])

  const firstPage = list.data?.pages[0]

  const excludedItems = useMemo(
    () => (all.data?.items ?? []).filter(isExcluded),
    [all.data?.items],
  )

  // 后端算好的真实数量。以前这里在有 frame 时改用夹具里写死的 0/3/5。
  const followCount = firstPage?.following_count ?? 0

  // 首屏加载用整页骨架。GET 已经回来是 []、SSE 却断着——那种
  // 「空」不是「现在什么都没发生」，不能在这里提前画 EmptyMatrix，
  // 否则重连条都看不见。交给 ActivityView：断条留着，底下继续等。
  if (list.isPending && !list.data && !list.error) return <ScreenLoading shape="timeline" width="reading" />

  return (
    <ActivityView
      items={merged}
      excludedItems={excludedItems}
      search={search}
      lockedProjectId={projectId}
      projectName={projectId ? project.data?.name : undefined}
      actionCount={firstPage?.requires_action_count ?? 0}
      followCount={followCount}
      omitted={list.data?.pages.reduce((sum, page) => sum + page.omitted, 0) ?? 0}
      disconnected={disconnected}
      lastUpdatedAt={lastUpdated.current ?? merged[0]?.occurred_at}
      recoveredCount={recoveredCount}
      recoveredDedupe={recoveredDedupe}
      resetNotice={resetNotice}
      listFailed={Boolean(list.error) || search.frame === 'error'}
      macos={search.frame === 'A14'}
      lastReadStreamSeq={firstPage?.last_read_stream_seq ?? 0}
      watermark={firstPage?.watermark ?? 0}
      hasMore={list.hasNextPage}
      loadingMore={list.isFetchingNextPage}
      onLoadMore={() => void list.fetchNextPage()}
      onRetry={() => void list.refetch()}
    />
  )
}

export function ProjectActivityScreen() {
  const { projectId } = useParams({ strict: false }) as { projectId: string }
  return <ActivityScreen projectId={projectId} />
}
